import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Activity, Download, KeyRound, LogOut, Mail, MessageSquareText, RefreshCw, Search, Users } from 'lucide-react';
import './AdminDashboard.css';

const LOCAL_API_ORIGIN = ['localhost', '127.0.0.1'].includes(window.location.hostname) ? 'https://vibes.terencela.com' : '';
const API = `${process.env.REACT_APP_BACKEND_URL || LOCAL_API_ORIGIN}/api`;
const TOKEN_KEY = 'vibeAdminTokenV1';

function formatDate(value) {
  if (!value) return 'Never';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function shortDate(value) {
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(new Date(`${value}T00:00:00Z`));
}

function Login({ onLogin, error, loading }) {
  const [secret, setSecret] = useState('');
  return (
    <main className="admin-login">
      <form onSubmit={(event) => { event.preventDefault(); onLogin(secret); }} className="admin-login-card">
        <div className="admin-kicker"><KeyRound size={16} /> OWNER ACCESS</div>
        <h1>VIBE<br />CONTROL ROOM</h1>
        <p>Enter the production admin secret to view stored tester data.</p>
        <label htmlFor="admin-secret">Admin secret</label>
        <input id="admin-secret" type="password" value={secret} onChange={(event) => setSecret(event.target.value)} autoComplete="current-password" autoFocus />
        {error && <div className="admin-error" role="alert">{error}</div>}
        <button type="submit" disabled={!secret || loading}>{loading ? 'CHECKING…' : 'OPEN DASHBOARD'}</button>
      </form>
    </main>
  );
}

function MetricCard({ icon: Icon, label, value, note, accent }) {
  return (
    <article className="admin-metric" style={{ '--metric-accent': accent }}>
      <div className="admin-metric-label"><Icon size={17} /> {label}</div>
      <strong>{value}</strong>
      <span>{note}</span>
    </article>
  );
}

function SparkBars({ values }) {
  const max = Math.max(1, ...values.map((item) => item.count));
  return (
    <div className="admin-spark" aria-label="Chats over the last 14 days">
      {values.map((item) => (
        <div className="admin-spark-column" key={item.date} title={`${item.date}: ${item.count}`}>
          <span>{item.count || ''}</span>
          <div style={{ height: `${Math.max(5, (item.count / max) * 100)}%` }} />
          <small>{shortDate(item.date)}</small>
        </div>
      ))}
    </div>
  );
}

export default function AdminDashboard() {
  const [token, setToken] = useState(() => {
    try { return sessionStorage.getItem(TOKEN_KEY) || ''; } catch { return ''; }
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(Boolean(token));
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [activeTab, setActiveTab] = useState('chats');

  const load = useCallback(async (secret = token, nextPage = page, nextQuery = query) => {
    if (!secret) return;
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API}/admin/dashboard`, {
        headers: { Authorization: `Bearer ${secret}` },
        params: { page: nextPage, pageSize: 50, q: nextQuery },
      });
      try { sessionStorage.setItem(TOKEN_KEY, secret); } catch {}
      setToken(secret);
      setData(response.data);
    } catch (requestError) {
      if (requestError.response?.status === 403) {
        try { sessionStorage.removeItem(TOKEN_KEY); } catch {}
        setToken('');
        setError('That admin secret is not valid.');
      } else {
        setError(requestError.response?.data?.detail || 'Dashboard data could not be loaded.');
      }
    } finally {
      setLoading(false);
    }
  }, [page, query, token]);

  useEffect(() => { if (token) load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = async () => {
    const response = await axios.get(`${API}/admin/dashboard`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { format: 'csv' },
      responseType: 'blob',
    });
    const url = URL.createObjectURL(response.data);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `vibe-chat-data-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const maxMode = useMemo(() => Math.max(1, ...(data?.modes || []).map((item) => item.count)), [data]);
  if (!token) return <Login onLogin={(secret) => load(secret, 1, '')} error={error} loading={loading} />;

  const summary = data?.summary || {};
  const chats = data?.chats || { rows: [], count: 0, page: 1, pageSize: 50 };
  const totalPages = Math.max(1, Math.ceil(chats.count / chats.pageSize));

  return (
    <main className="admin-shell">
      <header className="admin-header">
        <div>
          <span className="admin-kicker">SUPABASE CORE · VIBES</span>
          <h1>VIBE CONTROL ROOM</h1>
          <p>Live tester emails, conversations, outputs, modes, models, and latency.</p>
        </div>
        <div className="admin-actions">
          <button onClick={() => load()} disabled={loading}><RefreshCw size={16} className={loading ? 'spin' : ''} /> REFRESH</button>
          <button onClick={exportCsv}><Download size={16} /> EXPORT CSV</button>
          <button className="admin-logout" onClick={() => { try { sessionStorage.removeItem(TOKEN_KEY); } catch {} setToken(''); setData(null); }}><LogOut size={16} /> LOG OUT</button>
        </div>
      </header>

      {error && <div className="admin-error admin-global-error" role="alert">{error}</div>}

      <section className="admin-metrics">
        <MetricCard icon={Mail} label="Emails" value={summary.emailCount ?? '—'} note="Stored beta signups" accent="#FF0066" />
        <MetricCard icon={MessageSquareText} label="Chats" value={summary.chatCount ?? '—'} note="Inputs and outputs stored" accent="#FFE000" />
        <MetricCard icon={Users} label="Active testers" value={summary.activeEmailCount ?? '—'} note={`${summary.inactiveEmailCount ?? 0} signed up without a chat`} accent="#00FF88" />
        <MetricCard icon={Activity} label="Avg latency" value={summary.averageDurationMs ? `${(summary.averageDurationMs / 1000).toFixed(2)}s` : '—'} note="OpenAI request duration" accent="#FFFFFF" />
      </section>

      <section className="admin-grid">
        <article className="admin-panel admin-trend">
          <div className="admin-panel-heading"><div><span>ACTIVITY</span><h2>Chats in the last 14 days</h2></div><small>{data?.generatedAt ? `Updated ${formatDate(data.generatedAt)}` : ''}</small></div>
          <SparkBars values={data?.dailyChats || []} />
        </article>
        <article className="admin-panel admin-modes">
          <div className="admin-panel-heading"><div><span>MODE MIX</span><h2>Most used translators</h2></div></div>
          <div className="admin-mode-list">
            {(data?.modes || []).map((mode) => (
              <div key={mode.name} className="admin-mode-row">
                <div><span>{mode.name.replaceAll('_', ' ')}</span><strong>{mode.count}</strong></div>
                <div className="admin-mode-track"><i style={{ width: `${(mode.count / maxMode) * 100}%` }} /></div>
              </div>
            ))}
          </div>
        </article>
      </section>

      <section className="admin-data-panel">
        <div className="admin-data-toolbar">
          <div className="admin-tabs" role="tablist">
            <button className={activeTab === 'chats' ? 'active' : ''} onClick={() => setActiveTab('chats')}>CHATS <span>{chats.count}</span></button>
            <button className={activeTab === 'emails' ? 'active' : ''} onClick={() => setActiveTab('emails')}>EMAILS <span>{summary.emailCount ?? 0}</span></button>
          </div>
          {activeTab === 'chats' && (
            <form onSubmit={(event) => { event.preventDefault(); setPage(1); setQuery(search); load(token, 1, search); }} className="admin-search">
              <Search size={16} /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search email, text, mode, model" /><button>SEARCH</button>
            </form>
          )}
        </div>

        {loading && !data ? <div className="admin-empty">LOADING LIVE DATA…</div> : activeTab === 'chats' ? (
          <>
            <div className="admin-table-wrap">
              <table>
                <thead><tr><th>TIME</th><th>EMAIL</th><th>INPUT</th><th>OUTPUT</th><th>MODE</th><th>MODEL</th><th>LATENCY</th><th>DETAILS</th></tr></thead>
                <tbody>
                  {chats.rows.map((chat) => (
                    <tr key={chat.id}>
                      <td className="admin-nowrap">{formatDate(chat.created_at)}</td>
                      <td>{chat.email || <span className="admin-muted">No email</span>}</td>
                      <td><div className="admin-text-cell">{chat.input_text}</div></td>
                      <td><div className="admin-text-cell">{chat.output_text}</div></td>
                      <td><span className="admin-badge">{chat.persona_id}</span></td>
                      <td><span className="admin-model">{chat.model}</span></td>
                      <td className="admin-nowrap">{chat.duration_ms} ms</td>
                      <td>
                        <details className="admin-details">
                          <summary>VIEW</summary>
                          <dl>
                            <dt>Request ID</dt><dd>{chat.id}</dd>
                            <dt>Intent</dt><dd>{chat.intent}</dd>
                            <dt>Command</dt><dd>{chat.command}</dd>
                            <dt>Length</dt><dd>{chat.length_hint}</dd>
                            <dt>Source</dt><dd>{chat.request_source}</dd>
                            <dt>Profile context</dt><dd><pre>{chat.profile_context ? JSON.stringify(chat.profile_context, null, 2) : 'None'}</pre></dd>
                          </dl>
                        </details>
                      </td>
                    </tr>
                  ))}
                  {!chats.rows.length && <tr><td colSpan="8" className="admin-empty">No chats match this search.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="admin-pagination"><span>Page {page} of {totalPages} · {chats.count} rows</span><div><button disabled={page <= 1 || loading} onClick={() => { const next = page - 1; setPage(next); load(token, next, query); }}>PREVIOUS</button><button disabled={page >= totalPages || loading} onClick={() => { const next = page + 1; setPage(next); load(token, next, query); }}>NEXT</button></div></div>
          </>
        ) : (
          <div className="admin-table-wrap">
            <table>
              <thead><tr><th>SIGNED UP</th><th>EMAIL</th><th>IP</th><th>CHATS</th><th>LAST CHAT</th><th>RECORD ID</th></tr></thead>
              <tbody>{(data?.emails || []).map((email) => <tr key={email.id}><td className="admin-nowrap">{formatDate(email.created_at)}</td><td>{email.email}</td><td>{email.ip || <span className="admin-muted">Not recorded</span>}</td><td>{email.chat_count}</td><td className="admin-nowrap">{formatDate(email.last_chat_at)}</td><td>{email.id}</td></tr>)}</tbody>
            </table>
          </div>
        )}
      </section>
      <footer className="admin-footer">OWNER-ONLY · LIVE FROM SUPABASE CORE · SERVICE ROLE REMAINS SERVER-SIDE</footer>
    </main>
  );
}
